import type { RequestResponse } from '@/types/publicType'
import type {
  userInfoInterface,
  countryInterface,
  languageInterface,
  followingInterface,
  fansInterface,
  levelExperienceInterface,
  purseInterface,
  bannedStatusInterface
} from '@/types/apiType/userType'

/**
 * 获取用户信息
 * @param queryUid 目标uid
 * @param os 系统
 * @param deviceId 设备ID iOS客户端环境需传，iOS1.7.0版本后有值
 */
export function apiGetUserInfo(params: {
  queryUid: string | number
  os: 'web' | 'iOS'
  deviceId?: string
}): Promise<RequestResponse<userInfoInterface>> {
  return getFetchData({
    url: '/user/web/get',
    method: 'GET',
    params
  })
}

/**
 * 注销登录
 * @param {string} queryUid
 */
export function apiLogout(data: { access_token: string }) {
  return getFetchData({
    url: '/acc/web/logout',
    method: 'POST',
    data
  })
}

/**
 * 编辑个人信息
 * @param {string} avatar
 * @param {string} birth
 * @param {string} country
 * @param {string} nick
 * @param {string} userDesc
 */
export function apiUpdateUserInfo(data: {
  avatar?: string
  birth: string
  country: string
  nick: string
  userDesc: string
}) {
  return getFetchData({
    url: '/h5/u/web/update',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 获取国家列表
 */
export function apiGetCountryList(): Promise<
  RequestResponse<countryInterface[]>
> {
  return getFetchData({
    url: '/h5/u/country',
    method: 'GET'
  })
}

/**
 * 获取语言列表
 */
export function apiGetLanguageList(): Promise<
  RequestResponse<languageInterface[]>
> {
  return getFetchData({
    url: '/h5/u/languageList',
    method: 'GET'
  })
}

/**
 * 关注
 * @param {number} likedUid
 * @param {number} type 1关注2取消关注
 */
export function apiRelationLike(data: {
  likedUid: number
  type: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/relation/like',
    method: 'POST',
    data
  })
}

/**
 * 举报
 * @param {number} reportType 1政治2色情3广告4人身
 * @param {number} type 1政治2色情3广告4人身
 * @param {number} reportUid 自己的uid
 * @param {number} uid 被举报人的uid
 */
export function apiReport(data: {
  reportType: number | string
  type: number
  reportUid: number
  uid: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/u/tipOff',
    method: 'POST',
    data
  })
}

/**
 * 更新语言
 * @param {string} updateLanguage 要修改的值
 * @param {1} type 1为更新第一语言
 */
export function apiUpdateLanguage(data: {
  updateLanguage: string
  type: 1
}): Promise<RequestResponse<languageInterface[]>> {
  return getFetchData({
    url: '/h5/u/update/current/language',
    method: 'POST',
    data
  })
}

/**
 * 获取好友列表
 */
export function apiGetFriends(params: {
  pageNum: number
  pageSize: number
}): Promise<RequestResponse<Array<followingInterface>>> {
  return getFetchData({
    url: '/h5/relation/attention/friends',
    method: 'GET',
    params
  })
}

/**
 * 获取粉丝列表
 */
export function apiGetFans(params: {
  pageNum: number
  pageSize: number
}): Promise<RequestResponse<fansInterface>> {
  return getFetchData({
    url: '/h5/relation/fanslist',
    method: 'GET',
    params
  })
}

/**
 * 获取关注列表
 */
export function apiGetFollowing(params: {
  pageNum: number
  pageSize: number
}): Promise<RequestResponse<Array<followingInterface>>> {
  return getFetchData({
    url: '/h5/relation/following',
    method: 'GET',
    params
  })
}

/**
 * 拉入房间黑名单
 */
export function apiRelationAdd(data: {
  roomId: number
  targetUid: number
  isAdd: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/i/markChatRoomBlackList',
    method: 'POST',
    data
  })
}

/**
 * 获取用户等级经验
 * @param {3|6} type 3:财富等级
 */
export function apiGetUserLevelExperience(params: {
  type: 3 | 6
}): Promise<RequestResponse<levelExperienceInterface>> {
  return getFetchData({
    url: '/h5/l/exeperience/get',
    method: 'GET',
    params
  })
}

/**
 * 钱包
 */
export function apiGetPurse(): Promise<RequestResponse<purseInterface>> {
  return getFetchData({
    url: '/purse/query',
    method: 'GET'
  })
}

/**
 * 获取封禁状态
 */
export function apiCheckBanned(): Promise<
  RequestResponse<bannedStatusInterface>
> {
  return getFetchData({
    url: '/banned/checkBanned',
    method: 'GET'
  })
}
